import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, Download, Calendar, Server, Ticket, AlertCircle,
  ChevronDown, Loader2, Check, AlertTriangle, FileDown
} from 'lucide-react';
import { SiMicrosoftazure, SiAmazonaws, SiGooglecloud, SiDigitalocean } from 'react-icons/si';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Provider icons mapping
const PROVIDER_ICONS = {
  azure: SiMicrosoftazure,
  aws: SiAmazonaws,
  gcp: SiGooglecloud,
  digitalocean: SiDigitalocean
};

const PROVIDER_COLORS = {
  azure: 'text-blue-600',
  aws: 'text-orange-600',
  gcp: 'text-teal-600',
  digitalocean: 'text-sky-600'
};

// Month Picker Component
function MonthPicker({ value, onChange }) {
  const currentDate = new Date();
  const months = [];
  
  // Generate last 12 months
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    });
  }
  
  return (
    <div className="relative">
      <select
        data-testid="month-picker"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none w-full px-4 py-2.5 pr-10 border border-slate-200 rounded-lg bg-white focus:border-primary focus:ring-1 focus:ring-primary font-medium"
      >
        {months.map(m => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
    </div>
  );
}

// Loading Progress Component
function LoadingProgress({ steps }) {
  return (
    <div className="space-y-3">
      {steps.map((step, idx) => (
        <div key={idx} className="flex items-center gap-3">
          {step.status === 'loading' ? (
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          ) : step.status === 'done' ? (
            <Check className="w-5 h-5 text-green-500" />
          ) : step.status === 'error' ? (
            <AlertTriangle className="w-5 h-5 text-red-500" />
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-slate-200" />
          )}
          <span className={`text-sm ${step.status === 'loading' ? 'text-primary font-medium' : 'text-slate-600'}`}>
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// Metric Badge Component
function MetricBadge({ label, value, unit = '%', thresholdWarn = 70, thresholdCrit = 85 }) {
  if (value === undefined || value === null) return <span className="text-slate-400 text-xs">N/A</span>;
  const color = value > thresholdCrit ? 'text-red-600 bg-red-50' : value > thresholdWarn ? 'text-amber-600 bg-amber-50' : 'text-emerald-600 bg-emerald-50';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${color}`}>
      {typeof value === 'number' ? value.toFixed(1) : value}{unit}
    </span>
  );
}

// Server Metrics Detail Row
function ServerMetricsRow({ server, expanded, onToggle }) {
  const cpu = server.cpu || {};
  const memory = server.memory || {};
  const disks = server.disks || [];
  
  return (
    <>
      <tr 
        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <td className="py-2.5 px-3">
          <div className="flex items-center gap-2">
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`} />
            <span className="font-medium font-mono text-xs">{server.server_name}</span>
          </div>
        </td>
        <td className="py-2.5 px-3 text-slate-600 text-xs">{server.os || server.server_type || 'N/A'}</td>
        <td className="py-2.5 px-3">
          {cpu.available ? <MetricBadge value={cpu.average} /> : <span className="text-slate-400 text-xs">--</span>}
        </td>
        <td className="py-2.5 px-3">
          {memory.available ? <MetricBadge value={memory.average} /> : <span className="text-slate-400 text-xs">--</span>}
        </td>
        <td className="py-2.5 px-3">
          {disks.length > 0 ? (
            <MetricBadge value={Math.max(...disks.map(d => d.used_pct))} thresholdWarn={75} thresholdCrit={90} />
          ) : <span className="text-slate-400 text-xs">--</span>}
        </td>
        <td className="py-2.5 px-3">
          <MetricBadge value={server.availability_percent} unit="%" thresholdWarn={0} thresholdCrit={0} />
        </td>
      </tr>
      {expanded && (cpu.available || memory.available || disks.length > 0) && (
        <tr className="bg-slate-50/80">
          <td colSpan={6} className="px-6 py-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              {/* CPU Detail */}
              {cpu.available && (
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="font-semibold text-slate-700 mb-2">CPU Usage</p>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-slate-500">Average</span><span className="font-medium">{cpu.average}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Maximum</span><span className="font-medium">{cpu.maximum}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">95th Pctl</span><span className="font-medium">{cpu.p95}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Minimum</span><span className="font-medium">{cpu.minimum}%</span></div>
                  </div>
                </div>
              )}
              {/* Memory Detail */}
              {memory.available && (
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="font-semibold text-slate-700 mb-2">Memory Usage</p>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-slate-500">Average</span><span className="font-medium">{memory.average}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Maximum</span><span className="font-medium">{memory.maximum}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">95th Pctl</span><span className="font-medium">{memory.p95}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Minimum</span><span className="font-medium">{memory.minimum}%</span></div>
                  </div>
                </div>
              )}
              {/* Disk Detail */}
              {disks.length > 0 && (
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="font-semibold text-slate-700 mb-2">Disk Usage</p>
                  <div className="space-y-1.5">
                    {disks.map((d, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-slate-500 font-mono truncate max-w-[100px]" title={d.drive}>{d.drive}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${d.used_pct > 90 ? 'bg-red-500' : d.used_pct > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                              style={{ width: `${Math.min(d.used_pct, 100)}%` }}
                            />
                          </div>
                          <span className="font-medium w-12 text-right">{d.used_pct}%</span>
                        </div>
                        {(d.size_gb != null || d.used_gb != null || d.free_gb != null) && (
                          <div className="text-[10px] text-slate-400 text-right mt-0.5">
                            {d.size_gb != null ? `Size ${d.size_gb.toFixed(2)} GB` : ''}
                            {d.used_gb != null ? ` · Used ${d.used_gb.toFixed(2)} GB` : ''}
                            {d.free_gb != null ? ` · Free ${d.free_gb.toFixed(2)} GB` : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Preview Panel Component
function PreviewPanel({ data }) {
  const [expandedServers, setExpandedServers] = useState({});
  
  if (!data) return null;
  
  const ProviderIcon = PROVIDER_ICONS[data.cloud_provider] || SiMicrosoftazure;
  const providerColor = PROVIDER_COLORS[data.cloud_provider] || 'text-blue-600';
  
  const toggleServer = (idx) => {
    setExpandedServers(prev => ({ ...prev, [idx]: !prev[idx] }));
  };
  
  return (
    <div data-testid="preview-panel" className="space-y-6">
      {/* Summary Banner */}
      <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ProviderIcon className={`w-6 h-6 ${providerColor}`} />
            <div>
              <p className="font-semibold text-slate-900">
                {data.server_count} resource{data.server_count !== 1 ? 's' : ''} found
              </p>
              <p className="text-sm text-slate-600">
                {data.ticket_count} ticket{data.ticket_count !== 1 ? 's' : ''} during {data.report_period}
              </p>
            </div>
          </div>
          {data.site24x7_connected && (
            <span data-testid="site24x7-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Site24x7 Metrics
            </span>
          )}
        </div>
      </div>
      
      {/* Warnings */}
      {data.warnings && data.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Warnings
          </h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            {data.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Server Inventory with Metrics */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Server className="w-4 h-4 text-primary" />
          Resource Inventory & Performance
        </h3>
        
        {data.servers && data.servers.length > 0 ? (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Server Name</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-600">OS</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-600">CPU Avg</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Mem Avg</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Disk Max</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Uptime</th>
                </tr>
              </thead>
              <tbody>
                {data.servers.map((server, idx) => (
                  <ServerMetricsRow
                    key={idx}
                    server={server}
                    expanded={!!expandedServers[idx]}
                    onToggle={() => toggleServer(idx)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-slate-500 text-sm">No resources found. Check credentials or Site24x7 group mapping.</p>
        )}
      </div>
      
      {/* Tickets Summary */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Ticket className="w-4 h-4 text-primary" />
          Ticket Summary ({data.ticket_count})
        </h3>
        
        {data.tickets && data.tickets.length > 0 ? (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">ID</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Subject</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Status</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Received</th>
                </tr>
              </thead>
              <tbody>
                {data.tickets.slice(0, 10).map((ticket, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 font-mono text-xs">{ticket.id}</td>
                    <td className="py-2 px-3 truncate max-w-xs">{ticket.subject}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        ticket.status === 'Closed' ? 'bg-green-100 text-green-700' :
                        ticket.status === 'Resolved' ? 'bg-blue-100 text-blue-700' :
                        ticket.status === 'Open' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {ticket.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{ticket.received_time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.tickets.length > 10 && (
              <p className="text-sm text-slate-500 p-3 border-t border-slate-100">
                + {data.tickets.length - 10} more tickets
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No issues were reported during this period.</p>
        )}
      </div>
    </div>
  );
}

// Main Generate Page
export default function GeneratePage() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  
  // Editable fields
  const [executiveOverview, setExecutiveOverview] = useState('');
  const [announcements, setAnnouncements] = useState('');
  const [securityRecommendation, setSecurityRecommendation] = useState('');
  
  const [downloading, setDownloading] = useState(null);
  
  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/customers`);
        setCustomers(response.data);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      }
    };
    fetchCustomers();
  }, []);
  
  const handleFetchData = async () => {
    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }
    
    setLoading(true);
    setError(null);
    setPreviewData(null);
    
    const [year, month] = selectedMonth.split('-').map(Number);
    const customer = customers.find(c => c.id === selectedCustomer);
    
    setLoadingSteps([
      { label: 'Connecting to cloud provider...', status: 'loading' },
      { label: 'Fetching server inventory...', status: 'pending' },
      { label: 'Fetching Site24x7 metrics (CPU/Memory/Disk)...', status: 'pending' },
      { label: 'Fetching Freshdesk tickets...', status: 'pending' }
    ]);
    
    try {
      // Simulate progress steps
      await new Promise(r => setTimeout(r, 500));
      setLoadingSteps(prev => [
        { ...prev[0], status: 'done' },
        { ...prev[1], status: 'loading' },
        prev[2], prev[3]
      ]);
      
      await new Promise(r => setTimeout(r, 500));
      setLoadingSteps(prev => [
        prev[0],
        { ...prev[1], status: 'done' },
        { ...prev[2], status: 'loading' },
        prev[3]
      ]);
      
      const response = await axios.post(`${API_URL}/api/reports/preview`, {
        customer_id: selectedCustomer,
        year,
        month
      });
      
      setLoadingSteps(prev => [
        prev[0], prev[1],
        { ...prev[2], status: 'done' },
        { ...prev[3], status: 'done' }
      ]);
      
      setPreviewData(response.data);
      
      // Set default text for editable fields
      setExecutiveOverview(
        `Thank you for allowing us to provide you with a Monthly Service Report. This document outlines a summary of ${customer?.name} for the month of ${response.data.report_period} and provides recommendations, if needed, associated with optimizing the performance of your infrastructure.`
      );
      setAnnouncements('No important announcements for this month.');
      setSecurityRecommendation('No security recommendations for this month.');
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch data');
      setLoadingSteps(prev => prev.map(s => 
        s.status === 'loading' ? { ...s, status: 'error' } : s
      ));
    }
    
    setLoading(false);
  };
  
  const handleDownload = async (format) => {
    if (!previewData) return;
    
    setDownloading(format);
    
    const [year, month] = selectedMonth.split('-').map(Number);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/reports/download/${format}`,
        {
          customer_id: selectedCustomer,
          year,
          month,
          executive_overview: executiveOverview,
          announcements,
          security_recommendation: securityRecommendation
        },
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const customer = customers.find(c => c.id === selectedCustomer);
      const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short' });
      const filename = `${customer?.name?.replace(/\s+/g, '_')}_Monthly_Service_Report_${monthName}_${year}.${format}`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError(`Failed to download ${format.toUpperCase()}`);
    }
    
    setDownloading(null);
  };
  
  return (
    <div data-testid="generate-page" className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Controls */}
        <div className="lg:col-span-1 space-y-6">
          {/* Selection Card */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Generate Report
            </h2>
            
            {/* Customer Select */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Select Customer
              </label>
              <div className="relative">
                <select
                  data-testid="customer-select"
                  value={selectedCustomer}
                  onChange={(e) => { setSelectedCustomer(e.target.value); setPreviewData(null); }}
                  className="appearance-none w-full px-4 py-2.5 pr-10 border border-slate-200 rounded-lg bg-white focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  <option value="">Choose a customer...</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
            
            {/* Month Select */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                <Calendar className="w-4 h-4 inline mr-1" />
                Report Month
              </label>
              <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
            </div>
            
            {/* Fetch Button */}
            <button
              data-testid="fetch-data-btn"
              onClick={handleFetchData}
              disabled={loading || !selectedCustomer}
              className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Fetching Data...</>
              ) : (
                'Fetch Data'
              )}
            </button>
            
            {error && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>
          
          {/* Loading Progress */}
          {loading && loadingSteps.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Progress</h3>
              <LoadingProgress steps={loadingSteps} />
            </div>
          )}
          
          {/* Editable Fields */}
          {previewData && (
            <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
              <h3 className="font-semibold text-slate-900">Customize Report</h3>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Executive Overview
                </label>
                <textarea
                  data-testid="executive-overview-input"
                  value={executiveOverview}
                  onChange={(e) => setExecutiveOverview(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Important Announcements
                </label>
                <textarea
                  data-testid="announcements-input"
                  value={announcements}
                  onChange={(e) => setAnnouncements(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Security Recommendation
                </label>
                <textarea
                  data-testid="security-recommendation-input"
                  value={securityRecommendation}
                  onChange={(e) => setSecurityRecommendation(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                />
              </div>
            </div>
          )}
          
          {/* Download Buttons */}
          {previewData && (
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Download className="w-5 h-5 text-primary" />
                Download Report
              </h3>
              
              <div className="space-y-3">
                <button
                  data-testid="download-docx-btn"
                  onClick={() => handleDownload('docx')}
                  disabled={downloading}
                  className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {downloading === 'docx' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <FileDown className="w-5 h-5" />
                  )}
                  Download DOCX
                </button>
                
                <button
                  data-testid="download-pdf-btn"
                  onClick={() => handleDownload('pdf')}
                  disabled={downloading}
                  className="w-full py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {downloading === 'pdf' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <FileDown className="w-5 h-5" />
                  )}
                  Download PDF
                </button>
              </div>
              
              <p className="text-xs text-slate-500 mt-3">
                PDF conversion may take 10-20 seconds
              </p>
            </div>
          )}
        </div>
        
        {/* Right Panel - Preview */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-slate-200 p-6 min-h-[600px]">
            <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Server className="w-5 h-5 text-primary" />
              Data Preview
            </h2>
            
            {!previewData && !loading && (
              <div className="flex flex-col items-center justify-center h-[500px] text-slate-400">
                <FileText className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg font-medium">No data yet</p>
                <p className="text-sm">Select a customer and click "Fetch Data" to preview</p>
              </div>
            )}
            
            {loading && (
              <div className="flex flex-col items-center justify-center h-[500px]">
                <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
                <p className="text-slate-600 font-medium">Fetching cloud data...</p>
              </div>
            )}
            
            {previewData && <PreviewPanel data={previewData} />}
          </div>
        </div>
      </div>
    </div>
  );
}
